import { type Control, type UseFormWatch, useController } from 'react-hook-form';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Box, Column, Row } from '~/commonComponents';
import { UserProfession } from '~/enums/UserProfession';
import { getIsAllowOfficeToPostProcedure } from '~/utils/DentistUtils';
import { ReadOnlyField } from '../EditShift/ReadOnlyField';
import { AssistedHygieneField } from '../ShiftDetails/ShiftDetailsForm/fields/AssistedHygieneField';
import { ExpectedProceduresField } from '../ShiftDetails/ShiftDetailsForm/fields/ExpectedProcedures';
import { MealBreakDurationField } from '../ShiftDetails/ShiftDetailsForm/fields/MealBreakDurationField';
import { MealBreakPaidField } from '../ShiftDetails/ShiftDetailsForm/fields/MealBreakPaidField';
import { ProfessionalPayField } from '../ShiftDetails/ShiftDetailsForm/fields/ProfessionalPayField';
import { TimeFieldsWrapper } from '../ShiftDetails/ShiftDetailsForm/fields/TimeFieldsWrapper';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { type IEditOpenShiftFormValues } from './useEditOpenShift';

interface IEditOpenShiftFormProps {
  control: Control<IEditOpenShiftFormValues>;
  watch: UseFormWatch<IEditOpenShiftFormValues>;
  officeName: string;
  /** Office that owns this shift — drives the rate recommendation. */
  officeId: string;
}

export const EditOpenShiftForm = ({
  control,
  watch,
  officeName,
  officeId,
}: IEditOpenShiftFormProps) => {
  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');
  const professionCode = professionType ? UserProfession.getCode(professionType) : '';
  const isRDH = professionCode === UserProfession.RDH;
  const showProcedures = getIsAllowOfficeToPostProcedure(professionType, preferredSpecialty);

  const shiftControl = control as unknown as Control<IShiftDetailsFormValues>;

  const { field: isAssistedHygieneField } = useController({ name: 'isAssistedHygiene', control });
  const { field: arrivalField } = useController({ name: 'arrivalTime', control });
  const { field: departureField } = useController({ name: 'departureTime', control });
  const { field: mealBreakDurationField } = useController({ name: 'mealBreakDuration', control });
  const { field: isMealBreakPaidField } = useController({ name: 'isMealBreakPaid', control });
  const { field: rateField } = useController({ name: 'rate', control });

  return (
    <Column data-testid="edit-open-shift-form" data-cy="edit-open-shift-form" gap={20}>
      <Column gap={6}>
        <Typography as="span" bold size="body_large">
          Office:
        </Typography>
        <Typography as="span" numberOfLines={2}>
          {officeName}
        </Typography>
      </Column>

      <ReadOnlyField
        label="Professional Type:"
        suffix="(Read-Only)"
        value={professionType}
        testId="profession-type-read-only"
      />

      <ReadOnlyField
        label="Preferred Specialty:"
        suffix="(Read-Only)"
        value={preferredSpecialty?.replace('specialty_', '') || ''}
        testId="preferred-specialty-read-only"
      />

      {showProcedures && (
        <>
          <Box height={1.5} backgroundColor={colors.neutral[100]} />
          <ExpectedProceduresField
            control={shiftControl}
            professionType={professionType}
            preferredSpecialty={preferredSpecialty}
          />
          <Box height={1.5} backgroundColor={colors.neutral[100]} />
        </>
      )}

      {isRDH && (
        <AssistedHygieneField
          value={isAssistedHygieneField.value}
          onChange={isAssistedHygieneField.onChange}
        />
      )}

      <Typography as="span" bold size="heading_3" color={colors.neutral[600]} style={{ marginTop: 16 }}>
        Hours & Pay
      </Typography>
      <Box height={1.5} backgroundColor={colors.neutral[100]} style={{ marginTop: -8 }} />

      <TimeFieldsWrapper
        arrivalValue={arrivalField.value}
        onArrivalChange={arrivalField.onChange}
        departureValue={departureField.value}
        onDepartureChange={departureField.onChange}
      />

      <Row gap={30}>
        <Column flex={1}>
          <MealBreakDurationField
            value={mealBreakDurationField.value}
            onChange={mealBreakDurationField.onChange}
          />
        </Column>
        <Column flex={1}>
          <MealBreakPaidField
            value={isMealBreakPaidField.value}
            onChange={isMealBreakPaidField.onChange}
          />
        </Column>
      </Row>

      <ProfessionalPayField
        value={rateField.value}
        onChange={rateField.onChange}
        professionType={professionType}
        officeId={officeId}
      />
    </Column>
  );
};
